import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { notificationService } from '../../services/notificationService';

const ResetPassword: React.FC = () => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    if (resetToken) {
      setToken(resetToken);
      setIsValidToken(true);
    } else {
      setIsValidToken(false);
      notificationService.error('Invalid or missing reset token');
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.newPassword || !formData.confirmPassword) {
      notificationService.warning('Please fill in all fields');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      notificationService.error('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      notificationService.error('Password must be at least 8 characters long');
      return;
    }

    if (!token) {
      notificationService.error('Invalid reset token');
      return;
    }

    setIsLoading(true);

    try {
      await apiService.resetPassword(token, formData.newPassword);
      
      setIsSuccess(true);
      notificationService.success('Password reset successfully! Redirecting to login...');
      
      // Show success state for 3 seconds before redirect
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
      
    } catch (error: any) {
      console.error('Password reset error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to reset password. The token may be expired or invalid.';
      notificationService.error(errorMessage);
      setIsLoading(false);
    }
  };

  if (isValidToken === false) {
    return (
      <>
        <style>
          {`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            .error-container {
              animation: fadeInUp 0.8s ease-out;
            }
            .floating-icon {
              animation: float 3s ease-in-out infinite;
            }
          `}
        </style>
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          padding: '1rem',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div className="error-container" style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            padding: '3rem 2.5rem',
            width: '100%',
            maxWidth: '420px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            textAlign: 'center'
          }}>
            <div className="floating-icon" style={{ 
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              boxShadow: '0 8px 32px rgba(239, 68, 68, 0.4)',
              position: 'relative'
            }}>
              <i className="bi bi-exclamation-triangle" style={{ fontSize: '2.5rem', color: 'white' }}></i>
            </div>

            <h1 style={{ 
              color: '#1a202c', 
              fontWeight: 700, 
              marginBottom: '1rem',
              fontSize: '1.75rem',
              letterSpacing: '-0.025em'
            }}>Invalid Reset Link</h1>

            <p style={{ 
              color: '#64748b', 
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.6',
              marginBottom: '2rem'
            }}>
              This password reset link is invalid or has expired. Please request a new password reset link.
            </p>

            <button
              type="button"
              style={{
                width: '100%',
                padding: '1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                letterSpacing: '0.025em'
              }}
              onClick={() => window.location.href = '/'}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }}
            >
              <i className="bi bi-arrow-left" style={{ marginRight: '0.75rem' }}></i>
              Back to Login
            </button>
          </div>
        </div>
      </>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <>
        <style>
          {`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            @keyframes pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
            .success-container {
              animation: fadeInUp 0.8s ease-out;
            }
            .floating-icon {
              animation: float 3s ease-in-out infinite;
            }
            .pulse-icon {
              animation: pulse 2s ease-in-out infinite;
            }
          `}
        </style>
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          padding: '1rem',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div className="success-container" style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            padding: '3rem 2.5rem',
            width: '100%',
            maxWidth: '420px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            textAlign: 'center'
          }}>
            <div className="floating-icon pulse-icon" style={{ 
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.4)',
              position: 'relative'
            }}>
              <i className="bi bi-check-circle" style={{ fontSize: '2.5rem', color: 'white' }}></i>
            </div>

            <h1 style={{ 
              color: '#1a202c', 
              fontWeight: 700, 
              marginBottom: '1rem',
              fontSize: '1.75rem',
              letterSpacing: '-0.025em'
            }}>Password Updated!</h1>

            <p style={{ 
              color: '#64748b', 
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.6',
              marginBottom: '2rem'
            }}>
              Your password has been successfully updated. You will be redirected to the login page in a few seconds.
            </p>

            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '2rem'
            }}>
              <p style={{ 
                color: '#166534', 
                fontSize: '0.875rem',
                margin: 0,
                fontWeight: 500
              }}>
                <i className="bi bi-info-circle" style={{ marginRight: '0.5rem' }}></i>
                Redirecting to login page...
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          .reset-container {
            animation: fadeInUp 0.8s ease-out;
          }
          .floating-icon {
            animation: float 3s ease-in-out infinite;
          }
          .loading-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            animation: fadeInUp 0.3s ease-out;
          }
          .loading-content {
            background: white;
            padding: 2rem;
            border-radius: 16px;
            text-align: center;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
            animation: pulse 2s ease-in-out infinite;
          }
        `}
      </style>
      
      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #10b981',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <h3 style={{ 
              color: '#1a202c', 
              margin: '0 0 0.5rem 0',
              fontSize: '1.25rem',
              fontWeight: 600
            }}>
              Updating Password
            </h3>
            <p style={{ 
              color: '#64748b', 
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Please wait while we securely update your password...
            </p>
          </div>
        </div>
      )}

      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '1rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decorative elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '100px',
          height: '100px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 4s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '15%',
          right: '15%',
          width: '150px',
          height: '150px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite reverse'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '5%',
          width: '80px',
          height: '80px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          animation: 'float 5s ease-in-out infinite'
        }}></div>

        <div className="reset-container" style={{
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          padding: '3rem 2.5rem',
          width: '100%',
          maxWidth: '420px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          position: 'relative',
          opacity: isLoading ? 0.7 : 1,
          pointerEvents: isLoading ? 'none' : 'auto',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div className="floating-icon" style={{ 
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.4)',
              position: 'relative'
            }}>
              <i className="bi bi-shield-lock" style={{ fontSize: '2.5rem', color: 'white' }}></i>
              <div style={{
                position: 'absolute',
                inset: '-2px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                borderRadius: '22px',
                zIndex: -1,
                opacity: 0.3,
                filter: 'blur(8px)'
              }}></div>
            </div>
            <h1 style={{ 
              color: '#1a202c', 
              fontWeight: 700, 
              marginBottom: '0.5rem',
              fontSize: '2rem',
              letterSpacing: '-0.025em',
              background: 'linear-gradient(135deg, #1a202c 0%, #374151 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Set New Password</h1>
            <p style={{ 
              color: '#64748b', 
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.5'
            }}>Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="newPassword" style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                color: '#374151', 
                marginBottom: '0.75rem',
                letterSpacing: '0.025em'
              }}>
                <i className="bi bi-lock" style={{ marginRight: '0.5rem', color: '#10b981' }}></i>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  style={{
                    width: '100%',
                    padding: '1rem 3.5rem 1rem 1.25rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    background: '#fafbfc',
                    fontWeight: 400
                  }}
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  required
                  minLength={8}
                  disabled={isLoading}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.background = '#fafbfc';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label htmlFor="confirmPassword" style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                color: '#374151', 
                marginBottom: '0.75rem',
                letterSpacing: '0.025em'
              }}>
                <i className="bi bi-lock-fill" style={{ marginRight: '0.5rem', color: '#10b981' }}></i>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  style={{
                    width: '100%',
                    padding: '1rem 3.5rem 1rem 1.25rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    background: '#fafbfc',
                    fontWeight: 400
                  }}
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.background = '#fafbfc';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '1rem',
                background: isLoading ? '#e5e7eb' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: isLoading ? '#64748b' : 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '1.5rem',
                boxShadow: isLoading ? 'none' : '0 4px 15px rgba(16, 185, 129, 0.4)',
                letterSpacing: '0.025em'
              }}
              disabled={isLoading}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.4)';
                }
              }}
            >
              <i className="bi bi-check-circle" style={{ marginRight: '0.75rem' }}></i>
              Update Password
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: '#667eea',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'none',
                fontWeight: 600,
                transition: 'color 0.2s ease'
              }}
              onClick={() => window.location.href = '/'}
              disabled={isLoading}
              onMouseEnter={(e) => e.currentTarget.style.color = '#5a67d8'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#667eea'}
            >
              <i className="bi bi-arrow-left" style={{ marginRight: '0.5rem' }}></i>
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;